import { JSX } from "react";

import { ChevronRight } from "@mui/icons-material";
import { Grid, Typography, Divider, Box, Button, useTheme, Theme } from "@mui/material";
import { t } from "i18next";
import { Popup } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import Difference from "src/components/Difference/Difference";
import DifferenceChip from "src/components/Difference/DifferenceChip";
import { formatNum } from "src/lib/number.helpers";
import { type MeasuresSummarizedItemDto } from "src/types/dashboardDatasetDto";
import LocationType from "src/types/LocationType";

const MIN_WIDTH = 320;

const getStyles = (theme: Theme) => ({
  fullWidth: { xs: 12 },
  gridContainer: { width: MIN_WIDTH, alignItems: "center", spacing: 1 },
  popupTitle: {
    fontSize: { xs: theme.fontSizes.xs, sm: theme.fontSizes.sm },
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
  },
  popupComparisonTimeText: { fontSize: theme.fontSizes.xs, color: theme.palette.text.secondary },
  popupFootfallGrowthContainer: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  popupFootfallGrowthValuesContainer: { display: "flex", flexDirection: "columns", justifyContent: "end", alignItems: "center" },
  alignEnd: { textAlign: "end" },
  icon: { color: theme.palette.primary.main },
  openDashboardButton: { fontSize: { xs: theme.fontSizes.sm, sm: theme.fontSizes.sm } },
});

function calcualteDifference(item: MeasuresSummarizedItemDto): number {
  if (!item.currentValue || !item.pastValue || item.pastValue === 0) {
    return 0;
  }
  return ((item.currentValue - item.pastValue) / item.pastValue) * 100;
}

function SummarizedItemPopup({ item, dateRangeText }: { item: MeasuresSummarizedItemDto; dateRangeText: string }): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Popup minWidth={MIN_WIDTH}>
      <Grid container sx={getStyles(theme).gridContainer} spacing={1}>
        <Grid size={getStyles(theme).fullWidth}>
          <Typography sx={getStyles(theme).popupTitle}>{item.localName}</Typography>
        </Grid>
        <Grid size={getStyles(theme).fullWidth}>
          <Divider />
        </Grid>
        <Grid size={getStyles(theme).fullWidth}>
          <Typography sx={getStyles(theme).popupComparisonTimeText}>{dateRangeText}</Typography>
        </Grid>
        <Grid size={getStyles(theme).fullWidth}>
          <Box sx={getStyles(theme).popupFootfallGrowthContainer}>
            <Box>
              <Typography fontSize={theme.fontSizes.xs} color={theme.palette.text.primary}>
                {t("footfall growth")}
              </Typography>
            </Box>
            <Box sx={getStyles(theme).popupFootfallGrowthValuesContainer}>
              <Typography fontSize={theme.fontSizes.md} fontWeight={theme.typography.fontWeightMedium} color={theme.palette.text.primary}>
                {formatNum(item.currentValue ?? 0)}
              </Typography>
              <Difference row={item} fontSize={theme.fontSizes.xs} />
              <DifferenceChip difference={calcualteDifference(item)} fontSize={theme.fontSizes.xs} hasWhiteBackground ml={0} />
            </Box>
          </Box>
        </Grid>

        <Grid size={getStyles(theme).fullWidth} sx={getStyles(theme).alignEnd}>
          <Button
            sx={getStyles(theme).openDashboardButton}
            variant='text'
            size='small'
            color='primary'
            endIcon={<ChevronRight sx={getStyles(theme).icon} />}
            onClick={() => {
              if (item.type === LocationType.Mall) {
                navigate(`/center-dashboard/${item.locationId}`);
              }
              if (item.type === LocationType.Shop) {
                navigate(`/shop-dashboard/${item.locationId}`);
              }
              if (item.type === LocationType.Station) {
                navigate(`/station-dashboard/${item.locationId}`);
              }
            }}
          >
            {t("Open Dashboard")}
          </Button>
        </Grid>
      </Grid>
    </Popup>
  );
}

export default SummarizedItemPopup;
